import { SiteSurvey } from "api/types/siteSurvey";
import { useState } from "react";
import { Link } from "react-router-dom";
import { fetchJson } from "shared/fetch/fetchJson";
import Loading from "shared/loading";
import { useRunOnMount } from "shared/useRunOnMount/useRunOnMount";
import { AssignToTechModal } from "./AssignToTechModal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faShare } from "@fortawesome/free-solid-svg-icons/faShare";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { faBook } from "@fortawesome/free-solid-svg-icons/faBook";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faTrashRestore } from "@fortawesome/free-solid-svg-icons/faTrashRestore";

export function SurveyList() {
  const [showArchived, setShowArchived] = useState(false);
  const { getSurveyList, isLoading, surveyList, removeSurvey } =
    useSurveyList();
  const [surveyIdForAssignToTechModal, setSurveyIdForAssignToTechModal] =
    useState<string | undefined>(undefined);

  useRunOnMount(() => {
    getSurveyList(false);
  });

  async function toggleShowArchive() {
    setShowArchived(!showArchived);
    await getSurveyList(!showArchived);
  }

  function hideModal() {
    setSurveyIdForAssignToTechModal(undefined);
  }

  return (
    <div>
      {!surveyList.surveys && <Loading />}
      {surveyList.surveys && (
        <>
          {surveyIdForAssignToTechModal && (
            <AssignToTechModal
              onHide={hideModal}
              surveyId={surveyIdForAssignToTechModal}
            />
          )}
          <div className="d-flex justify-content-end my-2">
            <div>
              {isLoading && <Loading />}
              <label>
                <input
                  type="checkbox"
                  onChange={toggleShowArchive}
                  checked={showArchived}
                />

                <span className="px-2">Show Archived?</span>
              </label>
            </div>
          </div>
          <div className="p-2 bg-white rounded">
            <table className="table table-striped table-hover ">
              <thead className="table-light">
                <tr>
                  <th>Facility</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Last Modified</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                {surveyList.surveys.map((survey, index) => {
                  return (
                    <tr key={`EditSurvey-${index}-${survey.id}`}>
                      <td>{survey.clientName}</td>
                      <td>{survey.customer}</td>
                      <td>
                        <div>{survey.address}</div>
                        <div>{`${survey.city} ${survey.state} ${survey.zip}`}</div>
                      </td>
                      <td>
                        {new Date(survey.modifiedOn).toLocaleDateString()}
                      </td>
                      <td>
                        <Link
                          to={`/site/survey/edit?id=${survey.id}`}
                          className="btn btn-link"
                          title="Edit Survey"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        <button
                          className="btn btn-link"
                          onClick={(e) =>
                            setSurveyIdForAssignToTechModal(survey.id ?? "")
                          }
                          title="Send to Tech"
                        >
                          <FontAwesomeIcon icon={faShare} />
                        </button>

                        <Link
                          to={`/site/survey/report?id=${survey.id}`}
                          className="btn btn-link"
                          title="Create Report"
                        >
                          <FontAwesomeIcon icon={faBook} />
                        </Link>
                        <button
                          className="btn btn-link"
                          onClick={(e) => {
                            if (survey.id) removeSurvey(survey.id);
                          }}
                          title={
                            surveyList.isArchived ? "Unarchive" : "Archive"
                          }
                        >
                          {surveyList.isArchived ? (
                            <FontAwesomeIcon icon={faTrashRestore} />
                          ) : (
                            <FontAwesomeIcon icon={faTrash} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function useSurveyList() {
  const [isLoading, setIsLoading] = useState(false);
  const [surveyList, setSurveyList] = useState<SurveyListType>({
    surveys: undefined,
    isArchived: false,
  });

  async function getSurveyList(isArchived: boolean): Promise<void> {
    setIsLoading(true);
    const response = await fetchJson<SiteSurvey[]>(
      `/api/SiteSurvey/list?archived=${isArchived}`
    );
    setIsLoading(false);
    setSurveyList({ surveys: response.data, isArchived });
  }

  async function removeSurvey(surveyId: string) {
    await fetchJson<void>(
      `/api/SiteSurvey/ToggleArchived?siteSurveyId=${surveyId}`,
      { method: "post" }
    );
    const newSurveys = surveyList.surveys?.filter((s) => s.id !== surveyId);
    setSurveyList({ ...surveyList, surveys: newSurveys });
  }

  return { getSurveyList, removeSurvey, surveyList, isLoading };
}

type SurveyListType = {
  surveys: SiteSurvey[] | undefined;
  isArchived: boolean;
};
